import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { MediaImage } from '@atlaskit/media-image';
import type { MediaClientConfig, Auth as MediaAuth, AuthProvider } from '@atlaskit/media-core';
import type { UploadRejectionData } from '@atlaskit/media-picker';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import type { CoverPictureId } from '../pageCoverPictureTypes.ts';

// eslint-disable-next-line import/no-cycle
import { ImageWrapper } from './EditorPageCoverPicture.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingDiv = styled.div<{
	dynamicCoverImageHeight?: number;
}>({
	position: 'relative',
	height: '100%',
	background: token('color.background.accent.gray.subtlest'),
	borderRadius: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: (props) => `${props.dynamicCoverImageHeight}px`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: (props) => `-${props.dynamicCoverImageHeight}px`,
});

export type MediaCoverPictureProps = {
	showErrorFlag: () => void;
	coverPicture: React.MutableRefObject<{ draft: CoverPictureId | null }>;
	mediaConfig: {
		collection: string | undefined;
		authProvider: MediaClientConfig['authProvider'];
		onUploadRejection?: (data: UploadRejectionData) => boolean;
	} | null;
	dynamicCoverImageHeight?: number;
	ssrConfig:
		| {
				mode: 'server' | 'client';
				config: {
					authProvider: AuthProvider;
					initialAuth?: MediaAuth;
					enforceDataSecurityPolicy?: boolean;
				};
		  }
		| undefined;
	contentId: string;
	hasSSRConfigInSSRModeOrHydration?: boolean;
	isFixedWidthImageOption?: boolean;
	isAIGenerated?: boolean;
};

export const MediaCoverPicture = ({
	coverPicture,
	dynamicCoverImageHeight,
	showErrorFlag,
	mediaConfig,
	ssrConfig,
	contentId,
	hasSSRConfigInSSRModeOrHydration,
	isFixedWidthImageOption,
	isAIGenerated,
}: MediaCoverPictureProps) => {
	const mediaClientConfig = hasSSRConfigInSSRModeOrHydration
		? ssrConfig && ssrConfig.config
		: mediaConfig && {
				authProvider: mediaConfig.authProvider,
			};

	if (mediaClientConfig) {
		return (
			<MediaImage
				identifier={{
					mediaItemType: 'file',
					id: String(coverPicture.current.draft?.value.id),
					collectionName: hasSSRConfigInSSRModeOrHydration
						? `contentId-${contentId}`
						: mediaConfig?.collection,
				}}
				mediaClientConfig={mediaClientConfig}
				//force rerender image on id change until https://product-fabric.atlassian.net/browse/CXP-2899 is fixed
				key={String(coverPicture.current.draft?.value.id)}
				apiConfig={{
					allowAnimated: true,
					mode: 'full-fit' as const,
					upscale: false,
				}}
				ssr={hasSSRConfigInSSRModeOrHydration ? ssrConfig?.mode : undefined}
			>
				{({ data, loading, error }) => {
					if (loading) {
						// TODO: Leverage coverPictureState instead to determine if the image was AI generated
						// https://hello.jira.atlassian.cloud/browse/CCI-12712
						if (
							isAIGenerated &&
							isExperimentEnabled('confluence_aifc_header_image_generation_experiment')
						) {
							return null;
						}

						return (
							<LoadingDiv
								data-testid="cover-pic-loading"
								dynamicCoverImageHeight={dynamicCoverImageHeight}
							/>
						);
					}
					if (!data) {
						return null;
					}
					if (error) {
						showErrorFlag();
					}
					return (
						<ImageWrapper
							data-testid="actual-cover-picture"
							id="actual-cover-picture"
							// need this empty alt tag for decorative images
							alt=""
							src={data.src}
							coverPicturePosition={coverPicture.current.draft?.value.position}
							isFixedWidthImageOption={isFixedWidthImageOption}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
							className="cover-picture-wrapper-dangerously-set-inner-html"
						/>
					);
				}}
			</MediaImage>
		);
	}
};
